// ===== FILE: src-modern/hooks/useAutoSave.js =====
import { useEffect, useRef } from 'react';
import { useGenogram } from '../contexts/GenogramContext';
import { useCanvasOperations } from './useCanvasOperations';

export const useAutoSave = (interval = 30000) => { // 30 seconds default
  const { state, actions } = useGenogram();
  const { fitToCanvas } = useCanvasOperations();
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    if (!state.isDirty) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout
    saveTimeoutRef.current = setTimeout(() => {
      // Save to localStorage
      const data = {
        people: state.people,
        relationships: state.relationships,
        households: state.households,
        textBoxes: state.textBoxes,
        customAttributes: state.customAttributes || [],
        fileName: state.fileName,
        version: '2.0',
        savedAt: new Date().toISOString()
      };

      try {
        localStorage.setItem('genogram_autosave', JSON.stringify(data));
        console.log('Auto-saved at', new Date().toLocaleTimeString());
        actions.setDirty(false);
      } catch (error) {
        console.error('Auto-save failed:', error);
      }
    }, interval);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [state, actions, interval]);

  // Load auto-save on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('genogram_autosave');
      if (saved && state.people.length === 0) {
        const data = JSON.parse(saved);
        const shouldLoad = window.confirm(
          `Found auto-saved genogram from ${new Date(data.savedAt).toLocaleString()}. Load it?`
        );
        if (shouldLoad) {
          actions.loadData(data);

          // Auto-fit to canvas after loading auto-saved data
          setTimeout(() => {
            fitToCanvas();
          }, 500);
        } else {
          // User declined to load autosave, clear it
          localStorage.removeItem('genogram_autosave');
        }
      }
    } catch (error) {
      console.error('Error loading auto-save:', error);
    }
  }, [actions, fitToCanvas, state.people.length]); // Include dependencies
};